<!-- src/components/UI/AssetCard.vue -->
<template>
  <div
    :class="cardClasses"
    tabindex="0"
    aria-label="Carte d'asset"
    :style="{ cursor: !isEmpty ? 'pointer' : 'default' }"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <!-- État vide ---------------------------------------------------- -->
    <template v-if="isEmpty">
      <slot name="empty-state">
        <!-- Mode memostable spécifique -->
        <div v-if="viewMode === 'memostable'" class="flex items-center gap-2 text-gray-500 text-sm w-full">
          <slot name="memos-table-icon" :classes="classes" />
          <span>{{ emptyTitle }}</span>
        </div>
        <!-- Autres modes -->
        <div v-else class="flex flex-col items-center justify-center text-center w-full h-full opacity-60">
          <p class="text-gray-500 font-medium mb-1">{{ emptyTitle }}</p>
          <p class="text-sm text-gray-400 mb-4">{{ emptyMessage }}</p>
        </div>
      </slot>
    </template>

    <!-- Mode TABLE --------------------------------------------------- -->
    <template v-else-if="viewMode === 'table'">
      <div style="display: contents">
        <slot name="table-row" :classes="classes" />
      </div>
    </template>

    <!-- Mode MEMOSTABLE --------------------------------------------- -->
    <template v-else-if="viewMode === 'memostable'">
      <div class="flex items-center gap-3">
        <slot name="memos-table-icon" :classes="classes" />

        <div class="min-w-0 flex-1">
          <slot name="memos-table-content" :classes="classes" />
        </div>

    
      </div>
    </template>

    <!-- Mode DEFAULT ------------------------------------------------- -->
    <template v-else>
      <div class="flex-1 p-4 sm:p-5 relative min-h-50">
        <!-- Badge de propriété -->
        <div v-if="showOwnershipBadge && !isEmpty && (viewMode === 'default' || viewMode === 'table')" class="absolute top-2 right-2 flex items-center gap-1">
          <!-- Icône de confidentialité -->
          <div
            v-if="isConfidential"
            class="relative group"
          >
            <div class="w-6 h-6 rounded-full flex items-center justify-center bg-yellow-100 text-yellow-600">
              <LockIcon class="w-4 h-4" />
            </div>
            <div :class="classes.tooltip">
              <div :class="classes.tooltipContent">
                Asset confidentiel
              </div>
              <div :class="classes.tooltipArrow" />
            </div>
          </div>

          <!-- Affichage pour les assets appartenant au user : icône œil privé/public -->
          <div
            v-if="isOwned"
            class="relative group"
          >
            <div
              v-if="isPrivate"
              class="w-6 h-6 rounded-full flex items-center justify-center bg-red-100 text-red-600"
            >
              <EyeOffIcon class="w-4 h-4" />
            </div>
            <div
              v-else
              class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600"
            >
              <EyeIcon class="w-4 h-4" />
            </div>
            <div :class="classes.tooltip">
              <div :class="classes.tooltipContent">
                {{ isPrivate ? 'Asset privé' : 'Asset public' }}
              </div>
              <div :class="classes.tooltipArrow" />
            </div>
          </div>
          <!-- Affichage pour les assets publics d'autres users -->
          <div
            v-else-if="isPublic"
            class="relative group"
          >
            <div class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
              <GlobeIcon class="w-4 h-4" />
            </div>
            <div :class="classes.tooltip">
              <div :class="classes.tooltipContent">
                Asset public partagé
              </div>
              <div :class="classes.tooltipArrow" />
            </div>
          </div>
        </div>
        
        <slot :classes="classes" />
        <slot name="info" :classes="classes" />
      </div>

      <!-- footer -->
      <div class="px-4 sm:px-5 py-3 border-t border-gray-200 mt-auto">
        <div class="flex items-center justify-between">
          <slot name="footer-left" :classes="classes">
            <span
              v-if="createdAt"
              class="flex items-center gap-2"
              :class="classes.footerText"
            >
              <ClockIcon :class="classes.footerIcon" />
              {{ formatDate(createdAt) }}
            </span>
          </slot>

          <slot name="footer-right" :classes="classes">
            <div class="flex items-center gap-2">
              <!-- compteurs -->
              <div
                v-if="memosCount !== null"
                class="relative group"
              >
                <div class="flex items-center gap-1 text-gray-500">
                  <ClipboardIcon :class="classes.footerIcon" />
                  <span>{{ memosCount }}</span>
                </div>
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">
                    {{ memosCount }} mémo{{ (memosCount ?? 0) > 1 ? 's' : '' }}
                  </div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </div>

              <div
                v-if="materialEntitiesCount !== null"
                class="relative group"
              >
                <div class="flex items-center gap-1 text-gray-500">
                  <AmphoraIcon :class="classes.footerIcon" />
                  <span>{{ materialEntitiesCount }}</span>
                </div>
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">
                    {{ materialEntitiesCount }}
                    entité{{ materialEntitiesCount > 1 ? 's' : '' }} matérielle{{ materialEntitiesCount > 1 ? 's' : '' }}
                  </div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </div>

              <div
                v-if="digitalEntitiesCount !== null"
                class="relative group"
              >
                <div class="flex items-center gap-1 text-gray-500">
                  <CpuIcon :class="classes.footerIcon" />
                  <span>{{ digitalEntitiesCount }}</span>
                </div>
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">
                    {{ digitalEntitiesCount }}
                    entité{{ digitalEntitiesCount > 1 ? 's' : '' }} numérique{{ digitalEntitiesCount > 1 ? 's' : '' }}
                  </div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </div>

              <!-- actions standard -->
              <button
                v-if="showDuplicateButton && canEdit"
                :class="['relative group', classes.actionButton, classes.actionButtonEdit]"
                aria-label="Dupliquer"
                @click.stop="handleDuplicate"
              >
                <CopyIcon :class="classes.actionIconEdit" />
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">Dupliquer</div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </button>
              <button
                v-if="showEditButton && canEdit"
                :class="['relative group', classes.actionButton, classes.actionButtonEdit]"
                aria-label="Modifier"
                @click.stop="handleEdit"
              >
                <PencilIcon :class="classes.actionIconEdit" />
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">Modifier</div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </button>
              <button
                v-if="showDeleteButton && canDelete"
                :class="['relative group', classes.actionButton, classes.actionButtonDelete]"
                aria-label="Supprimer"
                @click.stop="handleDelete"
              >
                <TrashIcon :class="classes.actionIconDelete" />
                <div :class="classes.tooltip">
                  <div :class="classes.tooltipContent">Supprimer</div>
                  <div :class="classes.tooltipArrow" />
                </div>
              </button>
            </div>
          </slot>
        </div>
      </div>
    </template>

    <!-- MODAL Are-You-Sure ------------------------------------------ -->
    <ModalAreYouSure
      v-model="showDeleteModal"
      :title="deleteModalTitle"
      :message="deleteModalMessage"
      :confirm-text="deleteModalConfirmText"
      :cancel-text="deleteModalCancelText"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
import {
    Amphora as AmphoraIcon,
    Clipboard as ClipboardIcon,
    Clock as ClockIcon,
    Copy as CopyIcon,
    Cpu as CpuIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Globe as GlobeIcon,
    Lock as LockIcon,
    Pencil as PencilIcon,
    Trash as TrashIcon
} from 'lucide-vue-next';
import { computed, ref } from 'vue';

import ModalAreYouSure from '../utils/modal_areyousure.vue';
import type {
    AssetCardEmits,
    AssetCardProps,
    AssetCardSlots,
    CommonClasses
} from './types';

/* ------------------------------------------------------------------ */
/* props / slots / emits                                              */
/* ------------------------------------------------------------------ */
const props = withDefaults(defineProps<AssetCardProps>(), {
  viewMode: 'default',
  createdAt: null,
  memosCount: null,
  materialEntitiesCount: null,
  digitalEntitiesCount: null,
  emptyTitle: null,
  emptyMessage: null,
  tableGridClass: 'grid grid-cols-4 items-center',
  showEditButton: false,
  showDeleteButton: false,
  showDuplicateButton: false,
  deleteModalTitle: 'Confirmer la suppression',
  deleteModalMessage: 'Cette action est définitive.',
  deleteModalConfirmText: 'Supprimer',
  deleteModalCancelText: 'Annuler',
  isOwned: false,
  isPublic: false,
  isPrivate: false,
  isConfidential: false,
  showOwnershipBadge: true,
  userId: undefined,
  canEdit: true,
  canDelete: true
});

const emit = defineEmits<AssetCardEmits>();
defineSlots<AssetCardSlots>();

/* ------------------------------------------------------------------ */
/* state                                                              */
/* ------------------------------------------------------------------ */
const showDeleteModal = ref(false);

/* ------------------------------------------------------------------ */
/* classes utility (centralisé)                                       */
/* ------------------------------------------------------------------ */
const classes = computed<CommonClasses>(() => ({
  title: props.viewMode === 'table' ? 'font-medium text-gray-800 text-sm' : 'font-medium text-gray-800 text-lg',
  subtitle: 'text-gray-600',
  icon: 'h-4 w-4 text-gray-500',
  iconWithText: 'flex items-center gap-2 text-gray-600 mb-1',
  link: 'text-blue-600 hover:text-blue-800',
  linkIcon: 'h-4 w-4',
  footerText: 'text-sm text-gray-500',
  footerIcon: 'h-4 w-4 text-gray-500',
  actionButton: 'p-1.5 rounded hover:bg-gray-100 transition-colors duration-200',
  actionButtonEdit: 'text-blue-600 hover:text-blue-700',
  actionButtonDelete: 'text-red-600 hover:text-red-700',
  tooltip: 'invisible group-hover:visible absolute z-[9999] -top-12 left-1/2 -translate-x-1/2 px-3 py-1 pointer-events-none',
  tooltipContent: 'bg-gray-900 text-white text-xs rounded p-2 whitespace-nowrap',
  tooltipArrow: 'absolute left-1/2 -bottom-1 -translate-x-1/2 w-4 h-4 bg-gray-900 rotate-45',
  actionIconEdit: 'h-4 w-4 text-blue-600',
  actionIconDelete: 'h-4 w-4 text-red-600'
}));

/* ------------------------------------------------------------------ */
/* computed helpers                                                   */
/* ------------------------------------------------------------------ */
const isEmpty = computed(
  () => props.emptyTitle !== null && props.emptyTitle !== undefined && props.emptyTitle !== ''
);

const cardClasses = computed(() => {
  if (isEmpty.value) {
    if (props.viewMode === 'table')
      return 'bg-white rounded-lg shadow hover:shadow-md col-span-full py-4';
    if (props.viewMode === 'memostable')
      return 'bg-white rounded-lg shadow-sm p-2.5 flex items-start gap-3 min-h-0 !h-auto';
    return 'bg-white rounded-lg shadow h-full flex flex-col py-8 opacity-60';
  }

  if (props.viewMode === 'table')
    return ['bg-white px-4 py-2 hover:bg-gray-50 border-b border-gray-100', props.tableGridClass, 'items-center'].join(' ');
  if (props.viewMode === 'memostable')
    return 'bg-white rounded-lg shadow-sm p-2.5 flex items-start gap-3 min-h-0 !h-auto';
  return 'bg-white rounded-lg shadow hover:shadow-lg h-full flex flex-col min-h-30';
});

/* ------------------------------------------------------------------ */
/* methods                                                            */
/* ------------------------------------------------------------------ */
const handleClick = (): void => emit('click');
const handleEdit = (e: Event): void => {
  e.stopPropagation();
  emit('edit');
};
const handleDuplicate = (e: Event): void => {
  e.stopPropagation();
  emit('duplicate');
};
const handleDelete = (e: Event): void => {
  e.stopPropagation();
  showDeleteModal.value = true;
};
const confirmDelete = (): void => {
  showDeleteModal.value = false;
  emit('delete');
};
const cancelDelete = (): void => {
  showDeleteModal.value = false;
};

const formatDate = (date: string | Date | null): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/');
};
</script>

<style scoped>
/* Ajouter du CSS spécifique ici si vraiment nécessaire
   – privilégier les utilitaires Tailwind */
</style>
